import { createContext, useContext, type ReactNode } from 'react';

/** The saved item a Manage sheet would open for. */
export type ManageCookbookItem = {
  /** Recipes and groceries both route — the flow is item-type agnostic. */
  itemType: 'pairing' | 'product';
  itemId: number;
  title: string;
  /**
   * The item's artwork, when the surface has it to hand.
   *
   * Optional here but *required by the sheet*, which shows the customer what
   * they just saved. Surfaces holding only an id and a title — a bookmark
   * button handed `pairingId` and `pairingName` — omit it, and
   * `ManageCookbookHost` resolves it from `itemType` + `itemId` instead. That
   * keeps the artwork requirement in the one place that renders it, rather
   * than making every save surface thread an image prop through its parents.
   */
  imageUrl?: string;
};

interface ManageCookbookContextValue {
  /**
   * The default cookbook's name, for the save toast's copy — "Added to
   * Jennifer’s Saves".
   */
  defaultCookbookName: string;
  openManageForItem: (item: ManageCookbookItem) => void;
}

const ManageCookbookContext = createContext<ManageCookbookContextValue | null>(
  null
);

interface Props extends ManageCookbookContextValue {
  /**
   * `false` provides `null` — the same answer consumers get with no provider
   * mounted — without changing the element above `children`. A host that has
   * to decide at runtime (feature flag, viewport, audience) renders this once
   * and toggles it, rather than swapping between a provider and a fragment:
   * that swap changes the parent's type, and React remounts everything below.
   */
  isAvailable?: boolean;
  children: ReactNode;
}

/**
 * Lets any surface that can save an item open the "Manage" sheet, without
 * knowing the sheet exists.
 *
 * The dependency runs the wrong way for a direct import: `ManageCookbooksSheet`
 * lives in `app-mhr`, and `app-mhr` already depends on this package. So this
 * declares the hole and `app-mhr` fills it.
 *
 * That inversion also makes the provider the feature gate. A caller asks for
 * the context and gets `null` when customer cookbooks are off — no flag check,
 * and no notion of cookbook routing, needs to reach this package at all.
 *
 * Lives here rather than in `@hungryroot/feed` because Manage is not a feed
 * feature: the spec's routing scenarios say "when I tap the heart on a recipe"
 * without naming a surface, and every surface with a heart already depends on
 * this package. Hosting it in the feed is what scoped Manage to the feed.
 */
export function ManageCookbookProvider({
  defaultCookbookName,
  openManageForItem,
  isAvailable = true,
  children,
}: Props) {
  return (
    <ManageCookbookContext.Provider
      value={isAvailable ? { defaultCookbookName, openManageForItem } : null}
    >
      {children}
    </ManageCookbookContext.Provider>
  );
}

/** Null when cookbook routing is not available — see the provider. */
export function useManageCookbook(): ManageCookbookContextValue | null {
  return useContext(ManageCookbookContext);
}

/**
 * Turns an unsave into an invitation to Manage.
 *
 * Returns a predicate every heart calls before unsaving. When cookbook routing
 * is on and the item is already saved, it opens the Manage sheet and answers
 * `true` — meaning *stop, the tap is handled*. It answers `false` for a save,
 * or when cookbooks are off, and the caller does what it always did.
 *
 * Shared so the behaviour cannot drift per surface. A filled heart is no longer
 * a remove button anywhere: the comps route it into the sheet, where the unsave
 * can show what it will cost — the confirmation when the item is in several
 * cookbooks — instead of silently emptying them from a card.
 *
 * The predicate shape, rather than a wrapped onClick, is deliberate: each
 * surface builds its own toggle out of different mutations and analytics, so
 * this only claims the one decision they share.
 */
export function useUnsaveOpensManage(): (
  isSaved: boolean,
  item: ManageCookbookItem
) => boolean {
  const manageCookbook = useManageCookbook();

  return (isSaved, item) => {
    if (!isSaved || manageCookbook == null) {
      return false;
    }

    manageCookbook.openManageForItem(item);
    return true;
  };
}
